import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  FormArray,
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { EditorComponent } from '@tinymce/tinymce-angular';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { TagInputModule } from 'ngx-chips';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { CustomerAccountType } from 'src/app/_enums/customer-account-type.enum';
import { DocumentType } from 'src/app/_enums/document-type.enum';
import {
  BUSINESS_INSURANCES,
  getInsuranceTypeLabel,
  InsuranceType,
  PERSONAL_INSURANCES,
  sortInsuranceTypesByLabel,
} from 'src/app/_enums/insurance-type.enum';
import { OfferType } from 'src/app/_enums/offer.enums';
import { PolicyActionType } from 'src/app/_enums/policy-action-type.enum';
import {
  getPeriodicityByInsuranceType,
  getPeriodicityLabel,
  getPeriodicityMultiplier,
  Periodicity,
  ProposalState,
} from 'src/app/_enums/proposal.enums';
import { CustomValidators } from 'src/app/_helpers/CustomValidators';
import { LanguageCode } from 'src/app/_helpers/nationalities';
import { getFileName } from 'src/app/_helpers/utils';
import { FileSizePipe } from 'src/app/_pipes/file-size.pipe';
import { ProductService } from 'src/app/_services/product.service';
import { ProposalService } from 'src/app/_services/proposal.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import {
  APIResponse,
  DocumentUploadResponse,
} from 'src/app/_types/api-responses.types';
import { Lead } from 'src/app/_types/lead.types';
import { CategoryDocument, FormProduct } from 'src/app/_types/product.types';
import {
  ParseOfferPdfResponse,
  ProductDetails,
  ProposalDetails,
} from 'src/app/_types/proposals.type';
import { v4 } from 'uuid';

import { IconsModule } from '../../icons/icons.module';
import { PdfViewerModalComponent } from '../pdf-viewer-modal/pdf-viewer-modal.component';
import { SlideInModalComponent } from '../slide-in-modal/slide-in-modal.component';
import {
  ProductItemFormGroup,
  ProductSelectionComponent,
} from './product-selection/product-selection.component';

dayjs.extend(customParseFormat);

@Component({
  selector: 'app-add-proposal-modal',
  templateUrl: './add-proposal-modal.component.html',
  styleUrls: ['./add-proposal-modal.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    SlideInModalComponent,
    ProductSelectionComponent,
    PdfViewerModalComponent,
    PdfViewerModule,
    ReactiveFormsModule,
    FormsModule,
    IconsModule,
    FileSizePipe,
    TagInputModule,
    EditorComponent,
  ],
})
export class AddProposalModalComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('modal') modal!: SlideInModalComponent;

  @ViewChild('productSelectionComponent')
  productSelectionComponent!: ProductSelectionComponent;

  formId = `proposalForm-${v4()}`;

  @Output()
  proposalAdded = new EventEmitter();

  updating = false;

  proposalId!: string;

  /**
   * The offer documents already on the proposal, as the request has to name them
   * again: it replaces the whole set rather than adding to it. `documentId` is
   * null for a document recorded before the store held them — the request takes
   * ids, so such a document cannot be kept and has to be uploaded again.
   */
  existingDocs!: { url: string; documentId: number | null }[] | null;

  startDateSubscription!: Subscription;

  productListSubscription!: Subscription;

  periodicitySubscription!: Subscription;

  insuranceTypeSubscription!: Subscription;

  subcategoryChangeSubscription: Subscription;

  coversPeriodicitySubscription!: Subscription;

  @Input()
  preload = true;

  loading = true;

  openModal(proposal?: ProposalDetails) {
    if (!this.preload && !this.leadDetailsData) {
      this.loading = true;
      this.modal.openModal();
      this.getLeadDetails(false, () => {
        this.loading = false;
        this.cdr.detectChanges();
        this.initModalContent(proposal);
      });
      return;
    }
    this.loading = false;

    this.initModalContent(proposal);
    this.modal.openModal();
  }

  private initModalContent(proposal?: ProposalDetails) {
    if (this.leadDetailsData && this.leadDetailsData.status === 'CLOSED') {
      this.toastrService.error($localize`Demande déjà traitée`);
      this.closeModal();
      return;
    }

    this.offerInformations.reset();
    this.products.reset();
    this.productBenefictsForm.reset();
    this.keyFeatureForm.reset();
    this.policyDocumentsForm.reset();
    this.acceptedProposalFormGroup.reset();
    this.productList.clear();
    this.productSelectionComponent.resetProductSelection();
    this.files = [];
    this.existingDocs = null;
    if (proposal) {
      this.updating = true;
      this.proposalId = proposal.proposalId;
      this.proposalState =
        proposal.proposalState || ProposalState.PROPOSAL_SAVED;
      const policyDocs = proposal?.policyDetails?.policyDocs;
      if (policyDocs) {
        const urls = Array.isArray(policyDocs) ? policyDocs : [policyDocs];
        this.existingDocs = urls.map((url, index) => ({
          url,
          documentId: proposal.policyDetails?.policyDocIds?.[index] ?? null,
        }));
      }
      this.fromProposal(proposal);
    } else {
      this.updating = false;
      this.prefillForm();
    }
  }

  closeModal() {
    this.modal.closeModal(false);
  }

  submitted = false;

  @Input()
  disableNavigation = false;

  @Input()
  leadId!: string;

  leadDetailsData!: Lead;

  minStartDate: string = dayjs().format('YYYY-MM-DD');

  minEndDate: string = dayjs().format('YYYY-MM-DD');

  ngOnInit(): void {
    if (!this.leadId) this.leadId = this.route.snapshot.paramMap.get('leadId')!;
    if (this.preload) this.getLeadDetails();
    this.startDateSubscription =
      this.offerInformations.controls.startDate.valueChanges.subscribe(
        (startDate) => {
          if (startDate) this.minEndDate = startDate;
        }
      );
    this.productListSubscription =
      this.products.controls.productList.valueChanges.subscribe((pdata) => {
        this.setTotalAmount(pdata, this.products.value.coversPeriodicity);
        this.updateKeyFeatures();
      });
    this.periodicitySubscription =
      this.products.controls.periodicity.valueChanges.subscribe(
        (periodicityVal) => {
          this.setPremiumAmount(
            this.products.value.totalAmount ?? 0,
            periodicityVal as Periodicity
          );
        }
      );

    this.coversPeriodicitySubscription =
      this.products.controls.coversPeriodicity.valueChanges.subscribe(
        (coverPeriodicity) => {
          this.setTotalAmount(
            this.products.controls.productList.value,
            coverPeriodicity
          );
        }
      );

    this.subcategoryChangeSubscription =
      this.products.controls.subCategoryId.valueChanges.subscribe((value) => {
        if (value) this.getCgaDocuments(value);
      });
  }

  ngAfterViewInit() {
    this.insuranceTypeSubscription =
      this.offerInformations.controls.insuranceType.valueChanges.subscribe(
        (value) => {
          if (!value) return;
          // Reset product selection when insurance type changes
          this.products.controls.categoryId.reset();
          this.products.controls.subCategoryId.reset();
          this.products.controls.categoryName.reset();
          this.products.controls.categoryNameInFrench.reset();
          this.products.controls.categoryLogo.reset();
          this.products.controls.subCategoryName.reset();
          this.products.controls.subCategoryNameInFrench.reset();
          this.products.controls.product.reset();
          this.productList.clear();
          this.products.controls.coversPeriodicity.setValue(
            getPeriodicityByInsuranceType(value)
          );
        }
      );
  }

  constructor(
    private readonly fb: FormBuilder,
    private readonly proposalService: ProposalService,
    private readonly toastrService: ToastrService,
    private readonly router: Router,
    private readonly userAuthService: UserAuthService,
    private readonly cookieService: CookieService,
    private readonly route: ActivatedRoute,
    private readonly productService: ProductService,
    private readonly cdr: ChangeDetectorRef
  ) {}

  private _proposalState = ProposalState.PROPOSAL_SAVED;

  get proposalState(): ProposalState {
    return this._proposalState;
  }

  set proposalState(value: ProposalState) {
    this._proposalState = value;
    this.onProposalStateChange(value);
  }

  ProposalState = ProposalState;

  get showOfferSignatureDateField(): boolean {
    return this.updating && this.proposalState === ProposalState.SIGNED_DOCS;
  }

  offerInformations = this.fb.group(
    {
      offerNumber: new FormControl('', [Validators.required]),
      policyType: new FormControl('New Offer', [Validators.required]),
      insuranceType: new FormControl<InsuranceType>(null, [
        Validators.required,
      ]),
      startDate: new FormControl('', [Validators.required]),
      endDate: new FormControl('', [Validators.required]),
      keyTag: new FormControl([]),
      offerSignatureDate: new FormControl<string | null>(null),
    },
    {
      validators: [CustomValidators.dateAfter('endDate', 'startDate')],
    }
  );

  products = this.fb.group({
    partnerId: new FormControl('', []),
    categoryId: new FormControl('', [Validators.required]),
    subCategoryId: new FormControl('', [Validators.required]),
    categoryName: new FormControl('', [Validators.required]),
    categoryNameInFrench: new FormControl('', []),
    categoryLogo: new FormControl('', []),
    subCategoryName: new FormControl('', [Validators.required]),
    subCategoryNameInFrench: new FormControl('', []),
    product: new FormControl<string[]>([], [Validators.required]),
    agent: new FormControl('', []),
    periodicity: new FormControl('', [Validators.required]),
    premiumAmount: new FormControl(0, [Validators.required]),
    totalAmount: new FormControl(0, [Validators.required]),
    productList: this.fb.array<FormGroup<ProductItemFormGroup>>(
      [],
      [Validators.required]
    ),
    coversPeriodicity: new FormControl<Periodicity>(Periodicity.YEAR),
  });

  productBenefictsForm = new FormControl('', [Validators.required]);

  keyFeatureForm = new FormControl('', [Validators.required]);

  policyDocumentsForm = this.fb.array<DocumentUploadResponse>(
    [],
    [Validators.required]
  );

  acceptedProposalFormGroup = this.fb.group({
    policyId: new FormControl(''),
  });

  files!: File[];

  onFileUpload(event: Event) {
    const uploadedFiles: File[] = Array.from(
      (event.target as HTMLInputElement).files!
    );
    uploadedFiles.forEach((file) => {
      if (
        !this.files.some(
          (f) =>
            f.type === file.type && f.size === file.size && f.name === file.name
        )
      ) {
        this.files.push(file);
      }
    });
    (event.target as HTMLInputElement).value = null!;
  }

  removeFile(index: number) {
    this.files.splice(index, 1);
  }

  removeExistingDoc(index: number) {
    if (this.existingDocs) {
      this.existingDocs.splice(index, 1);
      if (this.existingDocs.length === 0) {
        this.existingDocs = null;
      }
    }
  }

  submitProposal() {
    if (!this.canSubmit()) return;
    this.submitted = true;

    if (
      this.files &&
      this.files.length > 0 &&
      this.leadDetailsData?.userId &&
      this.leadDetailsData?.memberId
    ) {
      this.userAuthService
        .uploadDocuments(
          this.files,
          this.leadDetailsData.userId,
          this.leadDetailsData.memberId,
          DocumentType.OTHER
        )
        .subscribe((uploadedDocuments) => {
          this.saveProposal(
            uploadedDocuments.map((document) => document.documentId)
          );
        });
    } else {
      this.saveProposal();
    }
  }

  /** The kept documents the request can name; one recorded before the store held them cannot be. */
  private keptDocumentIds(): number[] {
    return (this.existingDocs ?? [])
      .map((document) => document.documentId)
      .filter((documentId): documentId is number => documentId !== null);
  }

  saveProposal(uploadedDocumentIds?: number[]) {
    const keyTagData: string[] = [];
    const objs = this.offerInformations.value.keyTag!;
    if (objs?.length > 0) {
      objs.forEach((obj: FormControl) => {
        keyTagData.push(obj.value);
      });
    }
    const catProductArray = [];

    const productJson: Array<any> = this.products.value.productList
      ? this.products.value.productList
      : [];
    for (let i = 0; i < productJson.length; i++) {
      catProductArray.push({
        productId: productJson[i].productId,
        productName: productJson[i].productName,
        productNameInFrench: productJson[i].productNameInFrench,
        amount: productJson[i].amount,
        coverOrder: productJson[i].coverOrder,
        score: productJson[i].score,
        rootProductId: productJson[i].rootProductId,
        mainProductId: productJson[i].mainProductId,
      });
    }

    const memberArrayData: any = [];

    const { startDate } = this.offerInformations.value;
    const { endDate } = this.offerInformations.value;
    let formData: any = {
      partnerId: this.products.value.partnerId,
      offerType: OfferType.PERSONALIZED,
      insuranceType: this.offerInformations.value.insuranceType,
      leadId: this.leadId,
      policyType: this.offerInformations.value.policyType,
      startDate: new Date(startDate).toISOString(),
      endDate: new Date(endDate).toISOString(),
      periodicity: this.products.value.periodicity,
      coversPeriodicity: this.products.value.coversPeriodicity,
      premiumAmount: this.products.value.premiumAmount,
      policyVisibility: 'private',
      memberId:
        memberArrayData && memberArrayData.length > 0
          ? memberArrayData
          : [this.leadDetailsData?.memberDetails?.memberId],
      categoryDetails: {
        categoryId: this.products.value.categoryId,
        categoryName: this.products.value.categoryName,
        categoryNameInFrench: this.products.value.categoryNameInFrench,
        categoryLogo: this.products.value.categoryLogo,
        subCategoryId: this.products.value.subCategoryId,
        subCategoryName: this.products.value.subCategoryName,
        subCategoryNameInFrench: this.products.value.subCategoryNameInFrench,
        documents: this.selectedDocuments,
      },
      productDetails: {
        productList: catProductArray,
      },
      agentId: this.products.value.agent,
      totalAmount: this.products.value.totalAmount,
      proposalAction: 'SAVE',
      familyMemberId: this.leadDetailsData.memberId,
    };

    if (this.proposalState === ProposalState.POLICY_CREATED) {
      formData = {
        ...formData,
        ...this.acceptedProposalFormGroup.value,
        contractDoc: uploadedDocumentIds ?? [],
        proposalState: this.proposalState,
        offerId: 'no-offer',
        policyDocs: [],
        keyTags: [],
      };
    } else {
      formData = {
        ...formData,
        ...this.acceptedProposalFormGroup.value,
        offerId: this.offerInformations.value.offerNumber,
        keyFeatures: this.keyFeatureForm.value,
        productBenefits: this.productBenefictsForm.value,
        keyTags: keyTagData,
        policyDocs:
          uploadedDocumentIds && uploadedDocumentIds.length > 0
            ? uploadedDocumentIds
            : this.updating && this.existingDocs
              ? this.keptDocumentIds()
              : null,
      };
    }

    if (this.showOfferSignatureDateField) {
      const { offerSignatureDate } = this.offerInformations.value;
      formData.offerSignatureDate = offerSignatureDate
        ? new Date(offerSignatureDate).toISOString()
        : null;
    }

    if (!this.updating) {
      this.proposalService._addProposal(formData).subscribe({
        next: (result: any) => {
          if (result.status == 'SUCCESS') {
            if (this.proposalState === ProposalState.POLICY_CREATED) {
              this.toastrService.success($localize`Police créée avec succès`);
              if (result.proposalId && !this.disableNavigation)
                this.router.navigate(['/proposal/view', result.proposalId]);
            } else {
              this.toastrService.success($localize`Offre créée avec succès`);
              if (!this.disableNavigation)
                this.router.navigate(['/task/lead/proposal', this.leadId]);
            }
            this.proposalAdded.emit();
            this.closeModal();
            this.submitted = false;
          } else {
            this.toastrService.error(
              $localize`Une erreur est survenue lors de la création de l'offre`
            );
            this.submitted = false;
          }
        },
        error: (err: Error) => {
          let errRes: any;
          errRes = err;
          if (this.lngCode == 'en') {
            this.toastrService.error(errRes?.error?.error?.clientErrorMessage);
          } else {
            this.toastrService.error(
              errRes?.error?.error?.clientErrorMessageInFrench
            );
          }
          this.submitted = false;
        },
      });
    } else {
      formData.proposalId = this.proposalId;
      this.proposalService._updateProposal(formData).subscribe({
        next: (result: any) => {
          this.submitted = false;
          if (result.status == 'SUCCESS') {
            this.toastrService.success(
              $localize`Offre mise à jour avec succès`
            );
            this.closeModal();
            this.proposalAdded.emit();
          } else {
            this.toastrService.error(result.status);
          }
        },
        error: (err: any) => {
          this.submitted = false;
          this.toastrService.error(
            err?.error?.error?.clientErrorMessage ??
              $localize`Une erreur est survenue lors de la mise à jour`
          );
        },
      });
    }
  }

  canSubmit() {
    if (this.proposalState === ProposalState.POLICY_CREATED) {
      return (
        !this.submitted &&
        this.offerInformations.valid &&
        this.products.valid &&
        this.acceptedProposalFormGroup.valid &&
        this.files &&
        this.files.length > 0
      );
    }
    return (
      !this.submitted &&
      this.offerInformations.valid &&
      this.products.valid &&
      this.productBenefictsForm.valid &&
      this.keyFeatureForm.valid &&
      ((this.files && this.files.length > 0) ||
        (this.existingDocs && this.existingDocs.length > 0))
    );
  }

  onProposalStateChange(value: ProposalState) {
    if (value === ProposalState.POLICY_CREATED) {
      this.acceptedProposalFormGroup.controls.policyId.addValidators(
        Validators.required
      );

      this.offerInformations.controls.keyTag.reset();
      this.offerInformations.controls.keyTag.setErrors(null);
      this.offerInformations.controls.keyTag.clearValidators();

      this.offerInformations.controls.offerNumber.reset();
      this.offerInformations.controls.offerNumber.setErrors(null);
      this.offerInformations.controls.offerNumber.clearValidators();
      this.offerInformations.updateValueAndValidity();

      this.acceptedProposalFormGroup.updateValueAndValidity();
    } else if (value === ProposalState.PROPOSAL_SAVED) {
      this.acceptedProposalFormGroup.controls.policyId.reset();
      this.acceptedProposalFormGroup.controls.policyId.clearValidators();
      this.offerInformations.controls.offerNumber.addValidators(
        Validators.required
      );
      this.offerInformations.updateValueAndValidity();
      this.acceptedProposalFormGroup.updateValueAndValidity();
    }
  }

  getLeadDetails(checkStatus = false, onComplete?: () => void) {
    const obj = {
      leadId: this.leadId,
    };
    this.proposalService.getLeadDetails(obj).subscribe({
      next: async (result: APIResponse<Lead>) => {
        this.leadDetailsData = result.data;
        if (this.leadDetailsData.userId) this.getCustomerInsuranceTypes();
        if (checkStatus && this.leadDetailsData.status === 'CLOSED') {
          this.toastrService.error($localize`Demande déjà traitée`);
          if (!this.disableNavigation) this.router.navigate(['/proposal']);
          return;
        }
        onComplete?.();
      },
    });
  }

  lngCode =
    (this.cookieService.get('selectedCountryCode') as LanguageCode) || 'fr';

  // The covers periodicity field will be used to compute the total amount due
  // The (payment) periodicity will be used to compute the premium amount
  setTotalAmount(
    productsList: Partial<FormProduct>[],
    coversPeriodicity: Periodicity
  ) {
    let totalAmount = 0;
    for (let i = 0; i < productsList.length; i++) {
      totalAmount += !Number.isNaN(productsList[i].amount)
        ? (productsList[i].amount as number)
        : 0;
    }
    totalAmount *= getPeriodicityMultiplier(coversPeriodicity);
    this.products.controls.totalAmount.setValue(
      parseFloat(totalAmount.toFixed(2))
    );
    if (totalAmount > 0) {
      const { periodicity } = this.products.value;
      this.setPremiumAmount(totalAmount, periodicity as Periodicity);
    }
  }

  setPremiumAmount(totalAmount: number, periodicity: Periodicity = null) {
    this.products.controls.premiumAmount.setValue(
      parseFloat(
        (totalAmount / getPeriodicityMultiplier(periodicity)).toFixed(2)
      )
    );
  }

  get pstep1() {
    return this.offerInformations.controls;
  }

  get pstep2() {
    return this.products.controls;
  }

  removeCatProduct(i: number) {
    this.productList.removeAt(i);
  }

  get productList(): FormArray {
    return <FormArray>this.products.get('productList');
  }

  getInsuranceTypeLabel = getInsuranceTypeLabel;

  Periodicity = Object.values(Periodicity);

  getPeriodicityLabel = getPeriodicityLabel;

  /** Cancellations are opened through their own form, never as a proposition. */
  customerInsuranceTypes: InsuranceType[] = [];

  // The selected type (lead's or proposal's) stays listed even outside the
  // customer's types, so a prefilled select never goes blank.
  get insuranceTypeListData(): InsuranceType[] {
    const selected = this.offerInformations.controls.insuranceType.value;
    return !selected || this.customerInsuranceTypes.includes(selected)
      ? this.customerInsuranceTypes
      : [selected, ...this.customerInsuranceTypes];
  }

  getCustomerInsuranceTypes() {
    this.userAuthService.getUserDetails(this.leadDetailsData.userId).subscribe({
      next: (res) => {
        if (!res.data) return;
        const isCompany = res.data.type === CustomerAccountType.COMPANY;
        this.customerInsuranceTypes = sortInsuranceTypesByLabel(
          (isCompany ? BUSINESS_INSURANCES : PERSONAL_INSURANCES).filter(
            (type) =>
              type !== InsuranceType.CENTRALISATION &&
              type !== InsuranceType.CANCELLATION
          )
        );
      },
    });
  }

  fromProposal(proposal: ProposalDetails) {
    if (proposal.proposalState == ProposalState.POLICY_CREATED) {
      this.toastrService.error($localize`Une police a déjà été créée`);
      this.closeModal();
      return;
    }
    const keyTagData: any = [];
    if (proposal?.policyDetails?.keyTags.length > 0) {
      proposal?.policyDetails?.keyTags.forEach((obj: any) => {
        keyTagData.push({
          display: obj,
          value: obj,
        });
      });
    }
    this.minStartDate = dayjs(proposal?.policyDetails?.startDate).format(
      'YYYY-MM-DD'
    );

    this.minEndDate = dayjs(proposal?.policyDetails?.endDate).format(
      'YYYY-MM-DD'
    );

    const proposalFormStep1Obj = {
      partnerId: proposal.partnerId,
      offerNumber: proposal?.policyDetails?.offerId,
      policyType:
        proposal?.policyDetails?.policyType != 'New Offer'
          ? proposal?.policyDetails?.policyType
          : PolicyActionType.MODIFICATION_OF_THE_OFFER,
      insuranceType: proposal?.insuranceType,
      startDate: dayjs(proposal?.policyDetails?.startDate).format('YYYY-MM-DD'),

      endDate: dayjs(proposal?.policyDetails?.endDate).format('YYYY-MM-DD'),
      keyTag:
        keyTagData.length > 0 ? keyTagData : proposal?.policyDetails?.keyTags,
      offerSignatureDate: proposal.offerSignatureDate
        ? dayjs(proposal.offerSignatureDate).format('YYYY-MM-DD')
        : null,
    };

    this.offerInformations.patchValue(proposalFormStep1Obj, {
      emitEvent: false,
    });
    const productsObject = {
      agent: proposal?.agentDetails?.agentId,
      partnerId: proposal?.partnerId,
    };
    this.products.patchValue(productsObject, { emitEvent: false });
    const proposalFormStep2Obj = {
      categoryId: proposal?.policyDetails?.categoryDetails?.categoryId,
      subCategoryId: proposal?.policyDetails?.categoryDetails?.subCategoryId,
      categoryLogo: proposal?.policyDetails?.categoryDetails?.categoryLogo,
      categoryName: proposal?.policyDetails?.categoryDetails?.categoryName,
      categoryNameInFrench:
        proposal?.policyDetails?.categoryDetails?.categoryNameInFrench,
      subCategoryName:
        proposal?.policyDetails?.categoryDetails?.subCategoryName,
      subCategoryNameInFrench:
        proposal?.policyDetails?.categoryDetails?.subCategoryNameInFrench,
      agent: proposal?.agentDetails?.agentId,
      partnerId: proposal?.partnerId,
      periodicity: proposal?.policyDetails?.periodicity,
      coversPeriodicity:
        proposal?.policyDetails?.coversPeriodicity ??
        getPeriodicityByInsuranceType(proposal?.insuranceType),
    };
    this.products.patchValue(proposalFormStep2Obj, { emitEvent: false });
    const productSelectedData: string[] = [];

    proposal?.policyDetails?.productDetails?.productList.forEach((product) => {
      this.productList.push(this.createProductFormGroup(product));
      productSelectedData.push(
        this.lngCode === 'fr'
          ? product.productNameInFrench
          : product.productName
      );
    });
    const getProduct = {
      product: productSelectedData,
    };
    this.products.patchValue(getProduct);

    this.productBenefictsForm.setValue(
      proposal?.policyDetails?.productBenefits
    );
    this.keyFeatureForm.setValue(proposal?.policyDetails?.keyFeatures);

    this.getCgaDocuments(
      proposal?.policyDetails?.categoryDetails?.subCategoryId
    );

    if (proposal?.policyDetails?.categoryDetails?.documents)
      this.selectedDocuments =
        proposal?.policyDetails?.categoryDetails?.documents ?? [];

    if (
      this.productSelectionComponent &&
      this.productSelectionComponent.initializeFromFormGroup
    ) {
      this.productSelectionComponent
        .initializeFromFormGroup(
          false,
          this.offerInformations.controls.insuranceType.value
        )
        .subscribe({
          next: (success: boolean) => {
            if (!success) {
              this.toastrService.error(
                $localize`Erreur lors du chargement des produits`
              );
            }
          },
          error: () => {
            this.toastrService.error(
              $localize`Erreur lors du chargement des produits`
            );
          },
        });
    }
  }

  getFileName = getFileName;

  @ViewChild('pdfViewer') viewer!: PdfViewerModalComponent;

  viewFile(key: string) {
    if (key.includes('https://')) {
      this.setFileUrl(key);
    } else
      this.userAuthService.getPresignedUrl(key).subscribe((res) => {
        if (res.status === 'SUCCESS') {
          this.setFileUrl(res.data.preSignedUrl);
        }
      });
  }

  private setFileUrl(url: string) {
    this.viewer.pdfFileUrl = url;
    this.viewer.openModal();
  }

  prefillForm() {
    let startDate = dayjs();

    if (
      this.leadDetailsData?.metadata &&
      'startDate' in this.leadDetailsData.metadata
    ) {
      startDate = dayjs(this.leadDetailsData.metadata.startDate, 'DD/MM/YYYY');
    } else if ('insuranceStartDate' in this.leadDetailsData.metadata) {
      startDate = dayjs(
        this.leadDetailsData.metadata.insuranceStartDate,
        'DD/MM/YYYY'
      );
    }
    this.offerInformations.controls.policyType.setValue('New Offer');
    this.offerInformations.patchValue(
      {
        insuranceType: this.leadDetailsData.insuranceType,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: startDate.add(3, 'year').format('YYYY-MM-DD'),
      },
      { emitEvent: false }
    );

    this.products.patchValue(
      {
        agent:
          this.leadDetailsData.agentId != 'null'
            ? this.leadDetailsData.agentId
            : '',
        partnerId:
          this.leadDetailsData.partnerId != 'null'
            ? this.leadDetailsData.partnerId
            : '',
        coversPeriodicity: getPeriodicityByInsuranceType(
          this.leadDetailsData.insuranceType
        ),
      },
      { emitEvent: false }
    );
    if (this.leadDetailsData.originalProposalId) {
      this.userAuthService
        .GetProposalDetail({
          proposalId: this.leadDetailsData.originalProposalId,
        })
        .subscribe({
          next: (res) => {
            const originalProposal = res.data;
            if (
              originalProposal.policyDetails.categoryDetails &&
              originalProposal.policyDetails.productDetails
            ) {
              this.products.controls.categoryId.setValue(
                originalProposal.policyDetails.categoryDetails.categoryId,
                { emitEvent: true }
              );
            }
          },
        });
    }

    this.productSelectionComponent._getCategoryList(
      false,
      true,
      this.leadDetailsData.insuranceType
    );
  }

  cgaDocuments: CategoryDocument[] = [];

  getCgaDocuments(subcategory: string) {
    this.productService.getCategoryDocuments(subcategory, true).subscribe({
      next: (res) => {
        if (res?.data?.documents) this.cgaDocuments = res.data.documents;
        else {
          this.toastrService.error(
            res?.error?.clientErrorMessage ??
              $localize`Une erreur est survenue lors de la récupération des documents CGAs`
          );
        }
      },
      error: () => {
        this.toastrService.error(
          $localize`Une erreur est survenue lors de la récupération des documents CGAs`
        );
      },
    });
  }

  selectedDocuments: Omit<
    CategoryDocument,
    'categoryId' | 'createdOn' | 'active'
  >[] = [];

  toggleDocument(event: Event, document: CategoryDocument) {
    if (this.submitted) return;
    const isChecked = (<HTMLInputElement>event?.target).checked;
    if (isChecked) {
      this.selectedDocuments.push(document);
    } else {
      this.selectedDocuments = this.selectedDocuments.filter(
        (x) => x !== document
      );
    }
  }

  isDocumentSelected(document: CategoryDocument): boolean {
    return this.selectedDocuments?.some((x) => x.id === document.id);
  }

  ngOnDestroy(): void {
    if (this.startDateSubscription) this.startDateSubscription.unsubscribe();
    if (this.productListSubscription)
      this.productListSubscription.unsubscribe();
    if (this.periodicitySubscription)
      this.periodicitySubscription.unsubscribe();
    if (this.insuranceTypeSubscription)
      this.insuranceTypeSubscription.unsubscribe();
    if (this.subcategoryChangeSubscription)
      this.subcategoryChangeSubscription.unsubscribe();
    if (this.coversPeriodicitySubscription)
      this.coversPeriodicitySubscription.unsubscribe();
  }

  updateBenefits() {
    const benefits = `<ul>${this.productList.value
      .map((product) => {
        if (product.benefits) {
          return product.benefits
            .split('\n')
            .filter((benefit) => !!benefit)
            .map((benefit) => `<li>${benefit}</li>`)
            .join('');
        }
        return null;
      })
      .filter((benefit) => !!benefit)
      .join('')}</ul>`;
    this.productBenefictsForm.setValue(benefits);
  }

  updateKeyFeatures() {
    const products: FormProduct[] = this.productList.value;
    if (!products || products.length === 0) {
      this.keyFeatureForm.setValue('');
      return;
    }

    // Filter out subproducts that should be hidden (mainProductId !== rootProductId)
    // These are appended to their main product's display name and shouldn't be shown separately
    const displayableProducts = products.filter((product) => {
      const { mainProductId } = product;
      const { rootProductId } = product;

      // Hide products where mainProductId !== rootProductId (they're appended to main product)
      if (mainProductId && rootProductId && mainProductId !== rootProductId) {
        return false;
      }
      return true;
    });

    // Separate main products from remaining subproducts (root products with mainProductId === rootProductId)
    const mainProducts = displayableProducts.filter(
      (p) => !p.mainProductId || p.mainProductId === p.rootProductId
    );
    const subproducts = displayableProducts.filter(
      (p) => p.mainProductId && p.mainProductId !== p.rootProductId
    );

    // Sort products: main products first, then their subproducts grouped together
    const sortedProducts: FormProduct[] = [];

    // Add main products and their subproducts
    mainProducts.forEach((mainProduct) => {
      sortedProducts.push(mainProduct);
      // Find subproducts related to this main product
      const relatedSubproducts = subproducts.filter(
        (sp) =>
          sp.mainProductId === mainProduct.productId ||
          sp.rootProductId === mainProduct.productId
      );
      sortedProducts.push(...relatedSubproducts);
    });

    // Add any remaining subproducts that don't have a matching main product
    const addedSubproductIds = new Set(
      sortedProducts
        .filter((p) => p.mainProductId && p.mainProductId !== p.rootProductId)
        .map((p) => p.productId)
    );
    const remainingSubproducts = subproducts.filter(
      (sp) => !addedSubproductIds.has(sp.productId)
    );
    sortedProducts.push(...remainingSubproducts);

    // Generate HTML list items
    const listItems = sortedProducts
      .map((product) => {
        // Use displayProductName if available (contains appended subproduct name for main products)
        // Otherwise fall back to regular product name
        let productName: string;
        if (product.displayProductName) {
          productName = product.displayProductName;
        } else {
          productName =
            this.lngCode === 'fr'
              ? product.productNameInFrench || product.productName
              : product.productName || product.productNameInFrench;
        }

        const isSubproduct =
          product.mainProductId &&
          product.mainProductId !== product.rootProductId;
        const marginStyle = isSubproduct ? ' style="margin-left: 20px;"' : '';

        const price = Number(product.amount) || 0;
        const priceText = price > 0 ? ` - ${price} CHF` : '';

        return `<li${marginStyle}>${productName}${priceText}</li>`;
      })
      .join('');

    const keyFeatures = `<ul>${listItems}</ul>`;
    this.keyFeatureForm.setValue(keyFeatures);
  }

  handleProductSelectionChange() {
    this.updateKeyFeatures();
    this.updateBenefits();
  }

  importing = false;

  importedFileUrl: string;

  importSuccess = false;

  importErrors: string[] = [];

  handleImport(event: Event) {
    const uploadedFile: File = Array.from(
      (event.target as HTMLInputElement).files!
    )?.at(0);
    if (
      uploadedFile &&
      this.offerInformations?.controls?.insuranceType?.value
    ) {
      if (this.importing) return;
      this.importing = true;

      this.proposalService
        .parseOfferPdf({
          doc: uploadedFile,
          insuranceType: this.offerInformations.controls.insuranceType.value,
        })
        .subscribe({
          next: (res) => {
            this.files = [uploadedFile];
            this.importing = false;
            this.importSuccess = true;
            this.importedFileUrl = URL.createObjectURL(uploadedFile);

            this.fillProposalFromImport(res.data);
          },
          error: () => {
            this.toastrService.error(
              $localize`Une erreur est survenue lors de l'import du pdf de l'offre`
            );
            this.importing = false;
          },
        });

      // Call route and prefill form with response (handle error not found)
      // Display file side by side
    }
    (event.target as HTMLInputElement).value = null!;
  }

  fillProposalFromImport(importResult: ParseOfferPdfResponse) {
    this.importErrors = [];
    if (importResult.offerId) {
      this.offerInformations.controls.offerNumber.setValue(
        importResult.offerId
      );
    } else {
      this.importErrors.push(
        $localize`Le numéro de l'offre n'a pas pu être identifié`
      );
    }
    if (importResult.startDate) {
      this.offerInformations.controls.startDate.setValue(
        dayjs(importResult.startDate).format('YYYY-MM-DD')
      );
    } else {
      this.importErrors.push(
        $localize`La date de début n'a pas pu être identifiée`
      );
    }
    if (importResult.endDate) {
      this.offerInformations.controls.endDate.setValue(
        dayjs(importResult.endDate).format('YYYY-MM-DD')
      );
    } else {
      this.importErrors.push(
        $localize`La date de fin n'a pas pu être identifiée`
      );
    }
    if (importResult.periodicity) {
      this.products.patchValue({
        periodicity: Periodicity[importResult.periodicity],
      });
    } else {
      this.importErrors.push(
        $localize`La périodicité des paiements n'a pas pu être identifiée`
      );
    }
    if (importResult.categoryDetails) {
      this.products.patchValue(
        {
          categoryId: importResult.categoryDetails.categoryId,
          categoryLogo: importResult.categoryDetails.categoryLogo,
          categoryName: importResult.categoryDetails.categoryName,
          categoryNameInFrench:
            importResult.categoryDetails.categoryNameInFrench,
        },
        { emitEvent: false }
      );
      if (importResult.categoryDetails.subCategoryId) {
        this.products.patchValue(
          {
            subCategoryId: importResult.categoryDetails.subCategoryId,
            subCategoryName: importResult.categoryDetails.subCategoryName,
            subCategoryNameInFrench:
              importResult.categoryDetails.subCategoryNameInFrench,
          },
          { emitEvent: false }
        );
        this.productList.clear();
        importResult.products.forEach((product) => {
          this.productList.push(this.createProductFormGroup(product));
        });
        importResult.productsNotFound.forEach((x) => {
          this.importErrors.push(
            $localize`La couverture "${x.name} (${x.price} CHF)" n'a pas pu être identifiée`
          );
        });
        this.productSelectionComponent.initializeFromFormGroup(
          true,
          this.offerInformations.controls.insuranceType.value
        );
        this.updateBenefits();
      } else {
        this.importErrors.push(
          $localize`Le produit d'assurance n'a pas pu être identifiée`
        );
      }
    } else {
      this.importErrors.push(
        $localize`La compagnie d'assurance n'a pas pu être identifié`
      );
    }
  }

  private createProductFormGroup(product: ProductDetails) {
    // Determine the initial display name based on language
    const initialDisplayName =
      this.lngCode === 'en'
        ? (product.productName ?? product.productNameInFrench)
        : (product.productNameInFrench ?? product.productName);

    return this.fb.group({
      productId: [product.productId, [Validators.required]],
      productName: [product.productNameInFrench, [Validators.required]],
      productNameInFrench: [product.productNameInFrench, [Validators.required]],
      displayProductName: [initialDisplayName, [Validators.required]],
      amount: [Number(product.amount), [Validators.required]],
      coverOrder: [product.coverOrder ?? null],
      score: [product.score ?? null],
      rootProductId: [product.rootProductId ?? null],
      mainProductId: [product.mainProductId ?? null],
      benefits: [product.benefits ?? null],
    });
  }
}
